import { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFeatureVisibility, useVisibilityConfig } from '../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../utils/constants/visibilityKeys';
import { USER_TYPE } from '../../../utils/constants/user';
import useOpenMagicUpload from '../../../common/MagicUpload/useOpenMagicUpload';
import { MAGIC_LINK_CONFIG_NAME } from '../../../common/MagicUpload/Utils/MagicUploadConstants';
import { JOURNEY_TAB_CONFIG } from './constants';
import { UploadTab } from './uploadJourneyUtils';

export type JourneyUploadProps = {
    dropzoneVariant: 'journey';
    /**
     * Explicit MU-mode flag (RECUR-28067 review): tabs must branch on this, not on infer it from
     * the presence/absence of another prop (e.g. `acceptOverride === null` or
     * `!!onFilesSelectedOverride`), which breaks the moment an optional prop's shape changes.
     */
    isMagicUploadRouted: boolean;
    allowMultiple?: boolean;
    disabled?: boolean;
    acceptOverride?: string | null;
    onClickFunction?: () => void;
    onFilesSelectedOverride?: (files: File[]) => void;
};

type UseJourneyUploadRoutingArgs = {
    isInvestor: boolean;
    userType: string;
};

// Key used in the per-tab "opened via click" map for a call with no uploadTab (e.g. legacy
// callers that never pass one). Kept out of UPLOAD_TABS since it isn't a real tab.
const NO_TAB_KEY = '__no_tab__';

/**
 * The ONE branch point between the Magic Upload path and the legacy per-tab upload
 * chain on the borrower Data Upload tabs (RECUR-28067 D2). Called once in
 * UploadJourney; the resulting props are spread onto each in-scope tab's UploadCard.
 *
 * Mirrors the Topbar MU gate exactly, plus a config-load guard: while the visibility
 * config is loading (or failed to load), routing fails to legacy rather than blocking
 * the dropzone (a config fetch error would otherwise leave the tab unusable forever).
 */
const useJourneyUploadRouting = ({ isInvestor, userType }: UseJourneyUploadRoutingArgs) => {
    const [searchParams] = useSearchParams();
    const { isConfigLoaded } = useVisibilityConfig();
    const { magicUploadVisibility } = useFeatureVisibility({
        magicUploadVisibility: VISIBILITY_KEYS.MAGIC_UPLOAD.ENABLED,
    });
    const openMagicUpload = useOpenMagicUpload();
    // Click-then-pick fires onClickFunction (opens the drawer + tracks) immediately followed by
    // onFilesSelectedOverride (the file handoff) for the SAME borrower interaction — without this
    // flag both calls would track, double-counting one interaction as two opens. A drop skips
    // onClickFunction entirely, so it falls through to the untouched flag and still tracks once.
    //
    // Keyed per tab (review fix, RECUR-28067): a single shared flag meant a click on one tab,
    // cancelled at the OS picker (no onFilesSelectedOverride ever fires to reset it), left a
    // stale `true` that silently ate the tracking on the NEXT drop on any OTHER tab. Scoping the
    // flag per tab confines a stale click to its own tab.
    const openedViaClickRef = useRef<Partial<Record<string, boolean>>>({});

    const isMagicLink = searchParams.get('configName') === MAGIC_LINK_CONFIG_NAME;

    const isMagicUploadRouted =
        isConfigLoaded && magicUploadVisibility.show && !isInvestor && userType === USER_TYPE.COMPANY && !isMagicLink;

    const getJourneyUploadProps = (uploadTab?: UploadTab): JourneyUploadProps => {
        if (!isMagicUploadRouted) {
            return { dropzoneVariant: 'journey', isMagicUploadRouted: false };
        }
        const tabKey = uploadTab ?? NO_TAB_KEY;
        return {
            dropzoneVariant: 'journey',
            isMagicUploadRouted: true,
            allowMultiple: true,
            disabled: false,
            acceptOverride: null,
            onClickFunction: () => {
                openedViaClickRef.current[tabKey] = true;
                openMagicUpload({ uploadTab });
            },
            onFilesSelectedOverride: (files: File[]) => {
                const track = !openedViaClickRef.current[tabKey];
                openedViaClickRef.current[tabKey] = false;
                openMagicUpload({
                    files,
                    uploadTab,
                    fallbackDocType: uploadTab ? JOURNEY_TAB_CONFIG[uploadTab]?.fallbackDocType : undefined,
                    track,
                });
            },
        };
    };

    return { isMagicUploadRouted, getJourneyUploadProps };
};

export default useJourneyUploadRouting;
